import { DocumentTempResponse } from "apis/data-contracts";
import { CloseIcon } from "assets/icons";
import { Table } from "components/Common/Table/Table";
import Button from "components/foundation/Button";
import Modal from "components/foundation/Modal/Modal";
import Tab, { TabGroup } from "components/foundation/Tab";
import { DATE_FORMAT, DOCUMENT_UUID } from "config/constant";
import { ROUTER_PATH } from "config/path";
import dayjs from "dayjs";
import useResetWorkDocStates from "features/document/hooks/create/useResetWorkDocStates";
import { useDocumentCategories } from "features/document/hooks/queries/useDocumentCategoryQueries";
import { useTempDocumentsQuery } from "features/document/hooks/queries/useTempDocumentsQuery";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { isFormReselectedState } from "store/document/documentCommon";
import { workFormStepState } from "store/document/workDocument";
import styled from "styled-components";

const thListByCategory = [
  ["No.", "문서구분", "작성일"],
  ["No.", "지역구분", "작성일"],
  ["No.", "파일명", "작성일"],
];

interface TempDocumentModalProps {
  defaultTabIndex: number;
  onCancel: React.MouseEventHandler<HTMLButtonElement>;
}

const TempDocumentModal = ({
  defaultTabIndex,
  onCancel,
}: TempDocumentModalProps) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(defaultTabIndex);

  const { data: documentCategories } = useDocumentCategories();
  const { data: workTempDocuments } = useTempDocumentsQuery({
    categoryUuid: DOCUMENT_UUID.WORK.uuid,
  });
  const { data: analysisTempDocuments } = useTempDocumentsQuery({
    categoryUuid: DOCUMENT_UUID.ANALYSIS.uuid,
  });
  const { data: archivesTempDocuments } = useTempDocumentsQuery({
    categoryUuid: DOCUMENT_UUID.ARCHIVES.uuid,
  });

  const tempDocumentsList = [
    workTempDocuments,
    analysisTempDocuments,
    archivesTempDocuments,
  ];

  /* 임시저장 문서로 이동 */
  const navigate = useNavigate();

  const setIsFormReselected = useSetRecoilState(isFormReselectedState);
  const setWorkFormStep = useSetRecoilState(workFormStepState);

  const { resetWorkDocStates } = useResetWorkDocStates();

  /**
   * @description 테이블 행 클릭 이벤트 리스너
   */
  const onClickRow = (tempDocument: DocumentTempResponse) => {
    if (selectedTabIndex === 0) {
      navigate(ROUTER_PATH.document.work.create.form(tempDocument.uuid));

      resetWorkDocStates();
      setWorkFormStep(1);
    } else if (selectedTabIndex === 1) {
      navigate(ROUTER_PATH.document.analysis.create.form(tempDocument.uuid));
    } else if (selectedTabIndex === 2) {
      navigate(ROUTER_PATH.document.archive.create.form(tempDocument.uuid));
    }

    setIsFormReselected(false);
  };

  return (
    <ModalLayoutStyle>
      <ModalHeaderStyle>
        <TitleStyle>
          임시 저장된 문서가 있습니다.
          <DescriptionStyle>클릭시 해당 문서를 불러옵니다.</DescriptionStyle>
        </TitleStyle>

        <Modal.Close asChild>
          <button onClick={onCancel}>
            <CloseIcon />
          </button>
        </Modal.Close>
      </ModalHeaderStyle>

      <TabGroup>
        {documentCategories?.map((tab, index) => {
          const docCount = tempDocumentsList[index]?.length || 0;
          return (
            <Tab
              key={tab.uuid}
              selected={selectedTabIndex === index}
              onClick={() => setSelectedTabIndex(index)}
            >
              {`${tab.name} (${docCount})`}
            </Tab>
          );
        })}
      </TabGroup>

      <ModalBodyStyle>
        <TableStyle columns="16rem 1fr 22rem" width="100%">
          <Table.Thead>
            <Table.Tr>
              {thListByCategory[selectedTabIndex].map((th, index) => (
                <ThStyle key={index}>{th}</ThStyle>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tempDocumentsList[selectedTabIndex]?.map((tempDocument) => (
              <TbodyRowStyle
                key={tempDocument.uuid}
                onClick={() => onClickRow(tempDocument)}
              >
                <IndexTdStyle>
                  {String(tempDocument.index).padStart(5, "0")}
                </IndexTdStyle>
                <TitleTdStyle>{tempDocument.value}</TitleTdStyle>
                <CreatedAtTdStyle>
                  {dayjs(tempDocument.createdAt).format(DATE_FORMAT)}
                </CreatedAtTdStyle>
              </TbodyRowStyle>
            ))}
          </Table.Tbody>
        </TableStyle>
      </ModalBodyStyle>

      <ModalFooter>
        <Modal.Close asChild>
          <Button onClick={onCancel}>신규 등록 진행</Button>
        </Modal.Close>
      </ModalFooter>
    </ModalLayoutStyle>
  );
};

export default TempDocumentModal;

/* 스타일 */
const ModalLayoutStyle = styled(Modal.Layout)`
  width: 75rem;
`;

const ModalHeaderStyle = styled.header`
  display: flex;
  align-items: flex-start;
  gap: 2rem;
`;

const TitleStyle = styled(Modal.Title)`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex: 1;
`;

const DescriptionStyle = styled.p`
  ${({ theme }) => theme.typography.body1.regular}
  color: ${({ theme }) => theme.color.text.secondary};
`;

const ModalBodyStyle = styled(Modal.Content)`
  margin: 0;
  margin-bottom: 3rem;
`;

const TableStyle = styled(Table.Table)`
  border-bottom: none;
`;

const ThStyle = styled(Table.Th)`
  background: #444648;
`;

const TbodyRowStyle = styled(Table.Tr)`
  cursor: pointer;
`;

const IndexTdStyle = styled(Table.Td)`
  color: ${({ theme }) => theme.color.text.tertiery};
`;

const TitleTdStyle = styled(Table.Td)`
  color: ${({ theme }) => theme.color.text.default};
`;

const CreatedAtTdStyle = styled(Table.Td)`
  color: ${({ theme }) => theme.color.grey[700]};
`;

const ModalFooter = styled.footer`
  display: flex;
  justify-content: center;
`;
